import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, X, ZoomIn, ZoomOut, ArrowLeft, ArrowRight, Download, Calendar, ExternalLink } from 'lucide-react';

export default function Gallery() {
  const [cards, setCards] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [filter, setFilter] = useState('All');
  
  // Carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Lightbox states
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/GetGalleryCard`)
      .then(res => setCards(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error(err);
        setCards([]);
      });

    axios.get(`${API_BASE_URL}/GetCarouselImage`)
      .then(res => setCarouselImages(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error(err);
        setCarouselImages([]);
      });
  }, []);

  // Carousel auto-rotate effect
  useEffect(() => {
    if (!Array.isArray(carouselImages) || carouselImages.length === 0) return;
    const interval = setInterval(() => {
      setCarouselIndex(prev => (prev + 1) % carouselImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [carouselImages]);

  const handleNextSlide = (e) => {
    e.stopPropagation();
    setCarouselIndex(prev => (prev + 1) % carouselImages.length);
  };

  const handlePrevSlide = (e) => {
    e.stopPropagation();
    setCarouselIndex(prev => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  const openLightbox = (index) => {
    setActiveImageIndex(index);
    setZoomScale(1);
  };

  const closeLightbox = () => {
    setActiveImageIndex(null);
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setActiveImageIndex(prev => (prev > 0 ? prev - 1 : filteredItems.length - 1));
    setZoomScale(1);
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setActiveImageIndex(prev => (prev < filteredItems.length - 1 ? prev + 1 : 0));
    setZoomScale(1);
  };



  const safeCards = Array.isArray(cards) ? cards : [];
  const processedCards = safeCards.map(item => {
    return {
      ...item,
      category: item?.Category || item?.category || "Classroom Activities",
      date: item?.DateCreated ? new Date(item.DateCreated).toLocaleDateString('default', { month: 'short', year: 'numeric' }) : "July 2026"
    };
  });

  const filteredItems = processedCards.filter(item => {
    if (filter === 'All') return true;
    return item.category === filter;
  }).reverse();

  const categories = ['All', 'Annual Day', 'Sports', 'Classroom Activities', 'Celebrations', 'Trips', 'Art & Craft'];

  const getFeaturedEventName = (idx) => {
    const events = [
      { title: "Annual Day Function", desc: "Colorful cultural dance, music presentation and student drama stage plays." },
      { title: "Annual Sports Tournament", desc: "Fun races, balance challenges, gymnastics, and certificate awards." },
      { title: "Independence Day Celebration", desc: "National flag hoisting, patriotic songs, and sweet distributions." },
      { title: "Science & Craft Exhibition", desc: "Tactile model showcases, organic colors painting, and building blocks." }
    ];
    return events[idx % events.length];
  };

  return (
    <div style={{ minHeight: '90vh', background: 'var(--bg-gallery)', paddingTop: '120px', paddingBottom: '60px', fontFamily: 'Quicksand', position: 'relative', overflow: 'hidden' }}>
      
      {/* Playful floating illustrations background details (scrapbook style doodles) */}
      <div style={{ position: 'absolute', top: '5%', left: '3%', fontSize: '2.5rem', opacity: 0.15 }} className="cloud-float">☁️</div>
      <div style={{ position: 'absolute', top: '8%', right: '8%', fontSize: '3rem', opacity: 0.15 }} className="sun-spin">☀️</div>
      <div style={{ position: 'absolute', top: '15%', left: '10%', fontSize: '2rem', opacity: 0.15 }} className="float-slow">✏️</div>
      <div style={{ position: 'absolute', top: '20%', right: '15%', fontSize: '2.5rem', opacity: 0.12 }} className="cloud-float">🎈</div>
      <div style={{ position: 'absolute', top: '30%', left: '4%', fontSize: '2rem', opacity: 0.2 }} className="plane-float">✈️</div>
      <div style={{ position: 'absolute', top: '40%', right: '6%', fontSize: '2.5rem', opacity: 0.12 }} className="float-slow">🎨</div>
      <div style={{ position: 'absolute', top: '50%', left: '8%', fontSize: '2rem', opacity: 0.15 }} className="cloud-float">✨</div>
      <div style={{ position: 'absolute', top: '58%', right: '12%', fontSize: '2.2rem', opacity: 0.15 }} className="float-slow">✏️</div>
      <div style={{ position: 'absolute', top: '65%', left: '2%', fontSize: '2.8rem', opacity: 0.15 }} className="plane-float">🪁</div>
      <div style={{ position: 'absolute', top: '72%', right: '4%', fontSize: '2rem', opacity: 0.12 }}>📚</div>
      <div style={{ position: 'absolute', top: '80%', left: '6%', fontSize: '2.5rem', opacity: 0.15 }} className="cloud-float">🌈</div>
      <div style={{ position: 'absolute', top: '88%', right: '10%', fontSize: '2.2rem', opacity: 0.15 }} className="float-slow">🧸</div>
      <div style={{ position: 'absolute', bottom: '5%', left: '3%', fontSize: '2.5rem', opacity: 0.18 }} className="sun-spin">☀️</div>
      <div style={{ position: 'absolute', bottom: '8%', right: '7%', fontSize: '2rem', opacity: 0.15 }} className="cloud-float">☁️</div>
      <div style={{ position: 'absolute', bottom: '12%', left: '12%', fontSize: '2.3rem', opacity: 0.15 }} className="float-slow">🍎</div>
      <div style={{ position: 'absolute', bottom: '2%', right: '3%', fontSize: '2rem', opacity: 0.15 }}>🖍️</div>

      {/* Edge-to-edge layout (95% Screen Width) */}
      <div className="page-container-wide" style={{ width: '95%', maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', marginBottom: '40px' }}
        >
          <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--accent-purple)', textTransform: 'uppercase', letterSpacing: '1px' }}>School Memories</span>
          <h1 style={{ fontSize: '3rem', color: 'var(--text-primary)', marginBottom: '8px', fontFamily: 'Poppins', fontWeight: 800 }}>Digital Scrapbook</h1>
          <p style={{ color: 'var(--text-secondary)', fontWeight: 700, fontSize: '1.05rem' }}>Explore photos and moments from our annual programs, sports meets, and classroom achievements.</p>
        </motion.div>

        {/* Featured Memories Auto-Sliding Carousel */}
        {carouselImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            style={{ marginBottom: '56px', borderRadius: 'var(--border-radius-card)', overflow: 'hidden', boxShadow: 'var(--shadow-lg)', position: 'relative', height: '480px', backgroundColor: '#0b1d33', border: '8px solid #ffffff' }}
          >
            <AnimatePresence initial={false} mode="wait">
              <motion.div
                key={carouselIndex}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.6 }}
                style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
              >
                <img src={carouselImages[carouselIndex].Image} alt="Featured event" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }} />
                
                {/* Gradient description card details */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '40px 32px 32px', background: 'linear-gradient(transparent, rgba(11, 29, 51, 0.95))', color: '#ffffff' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--secondary-color)', backgroundColor: 'rgba(250,204,21,0.15)', padding: '6px 12px', borderRadius: '6px', marginBottom: '12px', display: 'inline-block', letterSpacing: '0.5px' }}>
                    Featured Highlight
                  </span>
                  <h3 style={{ color: '#ffffff', margin: '4px 0 8px 0', fontSize: '2rem', fontWeight: 800, fontFamily: 'Poppins' }}>
                    {getFeaturedEventName(carouselIndex).title}
                  </h3>
                  <p style={{ color: '#e2e8f0', margin: 0, fontSize: '1.05rem', fontWeight: 600, maxWidth: '650px', fontFamily: 'Quicksand' }}>
                    {getFeaturedEventName(carouselIndex).desc}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Manual Controls */}
            <button 
              onClick={handlePrevSlide}
              style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(255,255,255,0.9)', border: 'none', color: 'var(--text-primary)', width: '46px', height: '46px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10, boxShadow: 'var(--shadow-md)' }}
              className="carousel-arrow"
            >
              <ArrowLeft size={20} />
            </button>
            <button 
              onClick={handleNextSlide}
              style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(255,255,255,0.9)', border: 'none', color: 'var(--text-primary)', width: '46px', height: '46px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10, boxShadow: 'var(--shadow-md)' }}
              className="carousel-arrow"
            >
              <ArrowRight size={20} />
            </button>

            {/* Indicator dots */}
            <div style={{ position: 'absolute', top: '24px', right: '32px', display: 'flex', gap: '8px', zIndex: 10, backgroundColor: 'rgba(0,0,0,0.4)', padding: '6px 12px', borderRadius: '9999px' }}>
              {carouselImages.map((_, i) => (
                <div 
                  key={i} 
                  onClick={() => setCarouselIndex(i)}
                  style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: i === carouselIndex ? 'var(--secondary-color)' : 'rgba(255,255,255,0.4)', cursor: 'pointer', transition: 'background-color 0.2s' }} 
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* Category Chips Filters */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', marginBottom: '40px' }}
        >
          {categories.map((cat) => (
            <button 
              key={cat} 
              onClick={() => setFilter(cat)} 
              style={cat === filter ? activeChipStyle(cat) : chipStyle}
            >
              {cat === 'All' ? '🌟 All Memories' : cat}
            </button>
          ))}
        </motion.div>

        {/* Staggered Grid Scrapbook Layout */}
        <motion.div 
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '30px', width: '100%' }}
        >
          <AnimatePresence>
            {filteredItems.map((item, idx) => (
              <motion.div 
                key={item._id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                whileHover={{ scale: 1.02 }}
                style={cardWrapperStyle(item.category)}
                onClick={() => openLightbox(idx)}
                className="gallery-scrapbook-card"
              >
                <div style={{ position: 'relative', overflow: 'hidden', height: '260px' }}>
                  <img src={item.MainImage} alt={item.Description} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s ease' }} className="gallery-card-img" />
                  
                  {/* Photo tags */}
                  <span style={ribbonStyle(item.category)}>{item.category}</span>
                  
                  {/* Overlay zoom */}
                  <div style={overlayActionsStyle} className="overlay-actions">
                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#ffffff', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-md)' }}>
                      <Eye size={20} />
                    </div>
                  </div>
                </div>

                 <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: 700 }}>
                      <Calendar size={12} />
                      <span>Published {item.date}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)', fontWeight: 800, lineHeight: '1.4', fontFamily: 'Poppins' }}>
                      {item.Description}
                    </p>
                  </div>
                  {item.Link && (
                    <a 
                      href={item.Link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      onClick={(e) => e.stopPropagation()} 
                      className="btn-premium btn-premium-primary"
                      style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        fontSize: '0.82rem', 
                        padding: '10px 16px', 
                        width: 'fit-content', 
                        textDecoration: 'none',
                        borderRadius: '12px',
                        fontWeight: 700,
                        marginTop: '4px'
                      }}
                    >
                      <ExternalLink size={14} /> View All Photos
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredItems.length === 0 && (
          <div style={{ textAlign: 'center', padding: '64px 0', backgroundColor: '#ffffff', borderRadius: 'var(--border-radius-card)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-md)' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.05rem', fontWeight: 700 }}>No school moments belong to this category yet.</p>
          </div>
        )}

      </div>

      {/* Premium Fullscreen Lightbox */}
      <AnimatePresence>
        {activeImageIndex !== null && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
            style={lightboxBackdropStyle}
          >
            <button style={lightboxCloseStyle} onClick={closeLightbox}><X size={26} /></button>
            <button style={lightboxPrevStyle} onClick={handlePrevImage}><ArrowLeft size={24} /></button>
            <button style={lightboxNextStyle} onClick={handleNextImage}><ArrowRight size={24} /></button>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '90%', maxHeight: '80vh', gap: '20px' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '16px', border: '8px solid #ffffff', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}>
                <img 
                  src={filteredItems[activeImageIndex].MainImage} 
                  alt="active visual" 
                  style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', transform: `scale(${zoomScale})`, transition: 'transform 0.2s ease' }} 
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => setZoomScale(s => Math.min(s + 0.25, 3))} style={zoomButtonStyle}><ZoomIn size={18} /></button>
                <button onClick={() => setZoomScale(s => Math.max(s - 0.25, 0.5))} style={zoomButtonStyle}><ZoomOut size={18} /></button>
                <a href={filteredItems[activeImageIndex].MainImage} download target="_blank" rel="noreferrer" style={{ ...zoomButtonStyle, display: 'flex', textDecoration: 'none' }} title="Download Image"><Download size={18} /></a>
              </div>

              <div style={{ color: '#ffffff', textAlign: 'center', maxWidth: '600px' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--secondary-color)' }}>{filteredItems[activeImageIndex].category}</span>
                <p style={{ margin: '4px 0 0 0', fontSize: '1.25rem', fontWeight: 700, fontFamily: 'Poppins' }}>{filteredItems[activeImageIndex].Description}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

// Inline Styles
const chipStyle = {
  padding: '10px 20px',
  fontSize: '0.88rem',
  fontWeight: 700,
  borderRadius: '9999px',
  border: '1px solid var(--border-color)',
  backgroundColor: '#ffffff',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1.1)',
  fontFamily: 'Quicksand',
  boxShadow: 'var(--shadow-sm)'
};

const activeChipStyle = (cat) => {
  let bg = 'var(--primary-color)';
  if (cat === 'Annual Day') bg = 'var(--accent-coral)';
  if (cat === 'Sports') bg = 'var(--primary-color)';
  if (cat === 'Celebrations') bg = 'var(--secondary-color)';
  if (cat === 'Trips') bg = 'var(--accent-green)';
  if (cat === 'Art & Craft') bg = 'var(--accent-purple)';

  return {
    ...chipStyle,
    backgroundColor: bg,
    color: cat === 'Celebrations' ? 'var(--text-primary)' : '#ffffff',
    borderColor: bg,
    transform: 'scale(1.05)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
  };
};

const cardWrapperStyle = (category) => {
  let borderCol = 'var(--primary-color)';
  let shadowCol = 'rgba(96, 165, 250, 0.08)';
  if (category === 'Annual Day') { borderCol = 'var(--accent-coral)'; shadowCol = 'rgba(251,113,133,0.08)'; }
  if (category === 'Celebrations') { borderCol = 'var(--secondary-color)'; shadowCol = 'rgba(250,204,21,0.08)'; }
  if (category === 'Trips') { borderCol = 'var(--accent-green)'; shadowCol = 'rgba(74,222,128,0.08)'; }
  if (category === 'Art & Craft') { borderCol = 'var(--accent-purple)'; shadowCol = 'rgba(167,139,250,0.08)'; }

  return {
    backgroundColor: '#ffffff',
    borderRadius: 'var(--border-radius-card)',
    overflow: 'hidden',
    border: '1px solid var(--border-color)',
    borderTop: `6px solid ${borderCol}`,
    boxShadow: `0 12px 24px ${shadowCol}`,
    cursor: 'pointer',
    transition: 'all var(--transition-normal)'
  };
};

const ribbonStyle = (category) => {
  let bg = 'var(--primary-color)';
  let color = '#ffffff';
  if (category === 'Annual Day') bg = 'var(--accent-coral)';
  if (category === 'Celebrations') { bg = 'var(--secondary-color)'; color = 'var(--text-primary)'; }
  if (category === 'Trips') bg = 'var(--accent-green)';
  if (category === 'Art & Craft') bg = 'var(--accent-purple)';

  return {
    position: 'absolute',
    top: '16px',
    left: '16px',
    backgroundColor: bg,
    color: color,
    padding: '4px 12px',
    borderRadius: '6px',
    fontSize: '0.72rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    zIndex: 5,
    letterSpacing: '0.5px'
  };
};

const overlayActionsStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(11, 29, 51, 0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.25s ease',
  zIndex: 4
};

const lightboxBackdropStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(11, 29, 51, 0.95)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const lightboxCloseStyle = {
  position: 'absolute',
  top: '24px',
  right: '24px',
  background: 'transparent',
  border: 'none',
  color: '#ffffff',
  cursor: 'pointer',
  outline: 'none'
};

const lightboxPrevStyle = {
  position: 'absolute',
  left: '24px',
  background: 'rgba(255,255,255,0.1)',
  border: 'none',
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  color: '#ffffff',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none'
};

const lightboxNextStyle = {
  ...lightboxPrevStyle,
  left: 'auto',
  right: '24px'
};

const zoomButtonStyle = {
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  backgroundColor: 'rgba(255,255,255,0.1)',
  border: 'none',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  outline: 'none'
};

// Add standard scrapbook CSS triggers
const scrapbookStyles = document.createElement('style');
scrapbookStyles.innerHTML = `
  .gallery-scrapbook-card:hover .overlay-actions {
    opacity: 1 !important;
  }
  .gallery-scrapbook-card:hover .gallery-card-img {
    transform: scale(1.05) !important;
  }
  .carousel-arrow:hover {
    background-color: var(--secondary-color) !important;
    color: var(--text-primary) !important;
  }
`;
document.head.appendChild(scrapbookStyles);
